// services/loaderPerformance.js
// Secure read path for loader performance data.
// Calls get_loader_performance RPC — never writes to performance_ledger.
// Original IP — LoadDash Services LLC

import { supabase } from "./supabaseService";

// ── Label map (loader-facing, non-moralizing) ────────────────
const EVENT_LABELS = {
  job_completed:                "Job completed",
  crew_matched:                 "Crew matched",
  rebook_success:               "Rebooked",
  tip_received:                 "Tip received",
  cancel_after_accept:          "Canceled after accepting",
  no_show:                      "No show",
  issue_reported:               "Issue reported",
  major_complaint:              "Major issue",
  crew_mismatch_initial:        "Crew mismatch",
  crew_mismatch_subsequent:     "Crew mismatch",
  false_crew_declaration:       "False crew declaration",
  attempted_completion_blocked: "Completion blocked",
};

/**
 * Fetch the current loader's performance data.
 * Returns { loaderId, currentScore, currentBand, recentEvents } or throws.
 */
export async function fetchLoaderPerformance(loaderId) {
  const { data, error } = await supabase.rpc("get_loader_performance", {
    p_loader_id: loaderId,
  });

  if (error) throw new Error(error.message || "Failed to load performance data");
  if (!data) throw new Error("No performance data returned");

  return normalizePerformance(data);
}

/**
 * Normalize the RPC response into a stable shape for DriverPerformance.js.
 */
function normalizePerformance(raw) {
  return {
    loaderId:     raw.loader_id,
    currentScore: raw.current_score ?? 60,
    currentBand:  raw.current_band ?? "Standard",
    recentEvents: (raw.recent_events || []).map(normalizeEvent),
  };
}

function normalizeEvent(evt) {
  const evidence = evt.attribution_evidence || {};
  return {
    id:              evt.id,
    eventType:       evt.event_type,
    label:           EVENT_LABELS[evt.event_type] || evt.event_type,
    delta:           evt.delta,
    jobId:           evt.job_id,
    createdAt:       evt.created_at,
    decisionState:   evt.decision_state || null,
    sourceContext:   evt.source_context || null,
    isScoreAffecting: evt.is_score_affecting ?? false,
    reviewStatus:    evt.review_status || null,
    isReversed:      evt.is_reversed ?? false,
    reasonCode:      evidence.reason_code || null,
    reasonLabel:     evidence.reason_label || null,
    confidenceBand:  evidence.confidence_band || null,
  };
}
